import type { Mutable } from 'utility-types'

import type { ReadonlyNonEmptyArray, ReadonlyRecord } from '../../../utils'

import { Capability, ConnectwareError, ConnectwareErrorType, type StatusType } from '../../../domain'

import { mapRSTResourceStatusViewCurrentStatusType, mapRSTResourceStatusViewId, type StatusSupportedType, type StatusTypeFetcher } from '../../Connectware'
import type { HttpRequestArgs } from '../Base'
import { type NextBatch, RSTBatchedConnectwareHTTPService, type RSTBatchFetchingStrategy } from './Base'

type StatusFetchingArgs = Readonly<{ type: StatusSupportedType, resourceId: string }>
type SupportedEndpoints = '/api/v2/resources/states'
type StatusBatch = ReadonlyRecord<string, StatusType>

export class RSTStatusTypeHTTPService extends RSTBatchedConnectwareHTTPService<StatusFetchingArgs, SupportedEndpoints, 'GET', StatusBatch, StatusType>
    implements StatusTypeFetcher
{
    private readonly resourcesStrategy: Pick<RSTBatchFetchingStrategy<StatusFetchingArgs, '/api/v2/resources/states', 'GET'>, 'buildQueryParams'> = {
        buildQueryParams: (args) => ({ itemsPerPage: `${this.pageSize}`, resourceIds: args.map((args) => args.resourceId) }),
    }

    /**
     * These strategies here serve two purposes
     * - Allow for the extraction of statuses to be possible by many means
     * - Enables the scripts that check for endpoint usage (and associate them with capabilities) to detect these endpoints' usage properly
     */
    private readonly statesStrategies: ReadonlyRecord<StatusFetchingArgs['type'], RSTBatchFetchingStrategy<StatusFetchingArgs, SupportedEndpoints, 'GET'>> = {
        connection: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.CONNECTION_READ, ...this.resourcesStrategy },
        connections: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.CONNECTIONS_READ, ...this.resourcesStrategy },
        endpoint: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.ENDPOINT_READ, ...this.resourcesStrategy },
        endpoints: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.ENDPOINTS_READ, ...this.resourcesStrategy },
        mapping: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.MAPPING_READ, ...this.resourcesStrategy },
        mappings: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.MAPPINGS_READ, ...this.resourcesStrategy },
        nodes: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.NODES_READ, ...this.resourcesStrategy },
        server: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.SERVER_READ, ...this.resourcesStrategy },
        servers: { method: 'GET', path: '/api/v2/resources/states', capability: Capability.SERVERS_READ, ...this.resourcesStrategy },
    }

    protected resolveBatchFetchingStrategy (args: StatusFetchingArgs): RSTBatchFetchingStrategy<StatusFetchingArgs, SupportedEndpoints, 'GET'> {
        return this.statesStrategies[args.type]
    }

    protected createRequestArgs ({
        strategy: { buildQueryParams, ...strategy },
        requests,
    }: NextBatch<StatusFetchingArgs, SupportedEndpoints, 'GET', StatusType>): HttpRequestArgs<SupportedEndpoints, 'GET', StatusBatch> {
        return {
            ...strategy,
            authenticate: true,
            queryParams: buildQueryParams(requests.map((r) => r.args) as readonly StatusFetchingArgs[] as ReadonlyNonEmptyArray<StatusFetchingArgs>),
            handlers: {
                200: async (response) => {
                    const batch: Mutable<StatusBatch> = {}

                    const { data } = await response.getJson()
                    for (const { resource, state } of data) {
                        batch[mapRSTResourceStatusViewId(resource)] = mapRSTResourceStatusViewCurrentStatusType(state)
                    }

                    return batch
                },
                503: RSTStatusTypeHTTPService.createServerNotAvailableHandler(),
            },
        }
    }

    protected resolveBatchResponse (result: StatusBatch, { resourceId }: StatusFetchingArgs): StatusType | ConnectwareError {
        return result[resourceId] ?? new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Could not find status in batch', { resourceId })
    }

    fetchStatusType (type: StatusSupportedType, resourceId: string): Promise<StatusType> {
        return this.fetchBatched({ type, resourceId })
    }
}
